import wretch from "wretch";

export interface DealerLogo {
  fileName: string;
  hasCustomPadding: boolean;
  alt: string;
  businessName?: string;
  url?: string;
}
export interface DealerLogoData {
  primary: DealerLogo[];
  secondary: DealerLogo[];
}

export const EXCLUDED_LOGOS = [
  "moto3_logo_BA.webp",
  "bikeworld_logo_BA.webp",
  "motokeskus_logo_BA.webp",
  "gpgarageoy_logo_BA.webp"
];

export const isDealerLogoData = (data: unknown): data is DealerLogoData => {
  return (
    typeof data === "object" &&
    data !== null &&
    !Array.isArray(data) &&
    "primary" in data &&
    "secondary" in data &&
    Array.isArray(data.primary) &&
    Array.isArray(data.secondary)
  );
};

const DEALER_LOGOS_URL = import.meta.env.VITE_FRONTPAGE_DEALER_LOGOS_URL;
const LOGOS_LIST_FILENAME = import.meta.env.VITE_FRONTPAGE_DEALER_LOGOS_LIST;
const PRIMARY_LOGOS_PATH = import.meta.env
  .VITE_FRONTPAGE_DEALER_LOGOS_PRIMARY_PATH;
const SECONDARY_LOGOS_PATH = import.meta.env
  .VITE_FRONTPAGE_DEALER_LOGOS_SECONDARY_PATH;

// Breakpoint-based padding, so prerendered HTML is already right on mobile.
export const getResponsivePadding = (hasCustomPadding: boolean) =>
  hasCustomPadding ? { xs: 1, sm: 2 } : 0;

export const getPadding = (isMobile: boolean, hasCustomPadding: boolean) => {
  if (isMobile) {
    return hasCustomPadding ? 1 : 0;
  } else {
    return hasCustomPadding ? 2 : 0;
  }
};

export const getLogoUrl = (type: "primary" | "secondary", fileName: string) => {
  if (type === "primary") {
    return `${DEALER_LOGOS_URL}${PRIMARY_LOGOS_PATH}${fileName}`;
  } else {
    // type === "secondary"
    return `${DEALER_LOGOS_URL}${SECONDARY_LOGOS_PATH}${fileName}`;
  }
};

// Callers handle failures: the prerender loader degrades to empty lists and the
// homepage widget logs the error.
export const getDealerLogoData = async (): Promise<unknown> =>
  wretch(`${DEALER_LOGOS_URL}${LOGOS_LIST_FILENAME}`).get().json();

// Only plain file names, so the remote list can't point <img src> at another
// path or host.
const SAFE_FILE_NAME = /^[\w.-]+$/;

const isOptionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === "string";

/**
 * Returns the URL only when it is an absolute http(s) URL, so remote data can
 * never produce a `javascript:` or `data:` link.
 */
export const toSafeExternalUrl = (raw: unknown): string | undefined => {
  if (typeof raw !== "string" || raw.trim() === "") return undefined;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed.href
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Validates one entry of the remote logo list. Invalid entries return
 * `undefined` so callers can skip them without discarding the whole list; an
 * unsafe `url` is dropped but the logo is kept.
 */
export const sanitizeDealerLogo = (raw: unknown): DealerLogo | undefined => {
  if (typeof raw !== "object" || raw === null) return undefined;
  const entry = raw as Record<string, unknown>;
  const { fileName, alt, businessName } = entry;

  if (
    typeof fileName !== "string" ||
    !SAFE_FILE_NAME.test(fileName) ||
    fileName.includes("..") ||
    !isOptionalString(alt) ||
    !isOptionalString(businessName)
  ) {
    return undefined;
  }

  return {
    fileName,
    alt: alt ?? businessName ?? "",
    hasCustomPadding: entry.hasCustomPadding === true,
    businessName,
    url: toSafeExternalUrl(entry.url)
  };
};

export const shuffle = <T>(array: readonly T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
